import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const brakeWearSigns = [
    {
        id: 'signs',
        title: '1. Common Signs Your Brake Pads Are Worn',
        text: 'Brake pads rarely fail without warning. The most common signs include a high-pitched squealing while braking, a grinding noise during stops, or a feeling that the brake pedal is "softer" than usual. If the steering wheel vibrates when you apply pressure, your rotors might already be affected.'
    },
    {
        id: 'lifespan',
        title: '2. What Affects Brake Pad Lifespan',
        text: 'Most pads last between 30,000 and 70,000 miles. Stop-and-go city driving, heavy vehicle loads, and aggressive high-speed braking all accelerate wear. In contrast, highway driving involves much less friction and preserved pad life.'
    },
    {
        id: 'how-it-works',
        title: '3. How Brake Pad Replacement Works',
        text: 'The process involves lifting the car, removing the wheels, and opening the brake caliper to swap out the old pads for new ones. Professionals will also inspect the rotors for thinning or warping and ensure the braking system is properly lubricated and free of air.'
    },
    {
        id: 'diy-vs-mechanic',
        title: '4. DIY vs Mechanic: What Should You Do?',
        text: 'Brake pads are safety-critical. If you have the tools and experience, it’s a manageable DIY task. However, if you notice uneven wear patterns or deep grooves in your rotors, a professional mechanic is the safer choice to ensure the entire system is balanced.'
    }
];

const faqs = [
    {
        question: 'How long do brake pads usually last?',
        answer: 'Expect them to last 30,000 to 70,000 miles. Your specific driving habits and environment (like hilly terrain) play a huge role in the actual lifespan.'
    },
    {
        question: 'Can I drive with worn brake pads for a while?',
        answer: 'It is highly discouraged. Worn pads reduce stopping power and can quickly damage expensive rotors, turning a simple pad swap into a major brake overhaul.'
    },
    {
        question: 'Does grinding always mean the brake pads are gone?',
        answer: 'Usually, yes. Grinding means the friction material is completely gone and metal is rubbing against metal. This requires immediate attention to avoid total brake failure.'
    },
    {
        question: 'Do all four brake pads need to be replaced together?',
        answer: 'Not necessarily. Front brakes typically do 70% of the stopping and wear out faster. However, pads on the same axle (both fronts or both rears) must always be replaced in pairs.'
    }
];

const Blog18 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 18;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            Brake Pads Replacement: When and How to Change Them
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Most drivers don't think about brakes until they hear a squeal or feel a shudder. Caught early, it's a simple maintenance task.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 8 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/18_1.jpeg"
                            alt="Brake pad replacement and brake system inspection"
                            title="Brake Pads Replacement"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: brake_maintenance_inspection.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#signs" className="text-gray-900 no-underline hover:underline">Signs of wear</a></li>
                            <li><a href="#lifespan" className="text-gray-900 no-underline hover:underline">Lifespan factors</a></li>
                            <li><a href="#how-it-works" className="text-gray-900 no-underline hover:underline">How it works</a></li>
                            <li><a href="#diy-vs-mechanic" className="text-gray-900 no-underline hover:underline">DIY vs Mechanic</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            A slight squeal, a longer stop, or a vibration through the pedal can seem small at first, but brake pad wear usually builds gradually and gets more expensive when it is ignored.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Brake pads are what press against the rotor to slow the car down. As they wear thinner, stopping performance drops and heat builds up faster, potentially damaging more expensive components like rotors and calipers.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>When to replace?</strong><br />
                            Replace your brake pads when you hear squealing or when the friction material is below 3mm. Typically, this happens every 30,000 to 70,000 miles.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/18_2.jpeg"
                            alt="Brake components and worn brake pad comparison"
                            title="New vs worn brake pads"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: brake_pad_comparison.png
                        </figcaption>
                    </figure>

                    {brakeWearSigns.map((item) => (
                        <section key={item.id} id={item.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {item.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{item.text}</p>
                        </section>
                    ))}

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Braking performance is your car's most important safety feature. CarFido Assistant helps you monitor it without the guesswork.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Identify wear patterns based on mileage and driving environment</li>
                                <li>Follow guided checklists to inspect your own brakes safely</li>
                                <li>Store photos and service records of your brake replacements</li>
                                <li>Get clarity on whether a "squeal" is a minor noise or a major warning</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Brake pad replacement feels like a small job right up until it is ignored for too long. Paying attention to early signs protects your safety and your wallet.
                        </p>
                        <p className="mb-0 text-gray-700">
                            The drivers who stay ahead of brake problems are those who replace pads before wear spread into the rest of the system. If you hear squealing, check your pads today.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#signs" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Signs of wear</a></li>
                            <li><a href="#lifespan" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Lifespan</a></li>
                            <li><a href="#how-it-works" className="no-underline text-gray-600 hover:text-gray-900 text-sm">How it works</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog18;
